<script setup lang="ts">
import { h, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import CTemplateManagementActionsCellIconWrapper from './CTemplateManagementActionsCellIconWrapper.vue'
import useCanvasLocks from '@lib/composables/useCanvasLocks'

const props = defineProps<{
  hideCopy?: boolean
  hideEdit?: boolean
  hideDelete?: boolean
  hideMove?: boolean
  hideView?: boolean
  hideViewDetails?: boolean
  hideViewNewTab?: boolean
  hideCreateFromTemplate?: boolean
  hideToggleDefault?: boolean
  hideToggleStatus?: boolean
  isCopying?: boolean
  isDeleting?: boolean
  isDefault?: boolean
  isDraft?: boolean
  hideLock?: boolean
  canvasId: string
}>()

const emit = defineEmits<{
  copy: []
  edit: []
  delete: []
  move: []
  view: []
  viewNewTab: []
  viewDetails: []
  duplicate: []
  createFromTemplate: []
  toggleDefault: []
  toggleStatus: []
  lock: []
  unlock: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const { isCanvasLocked, isLockedByCurrentUser, isOrgAdmin } = useCanvasLocks()
const isLocked = computed(() => isCanvasLocked(props.canvasId))
const isLockedByMe = computed(() => isLockedByCurrentUser(props.canvasId))

const FONT_SIZE_CLASS = 'font-size-4'

// Get the actions menu options
const actionsMenu = computed(() => {
  const menu: DropdownMixedOption[] = []

  if (!props.hideView) {
    menu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.view')),
      key: 'view',
      icon: () => h(CIcon, { icon: 'eye' }),
    })
  }

  if (!props.hideViewDetails)
    menu.push({
      label: () =>
        h(
          'div',
          { class: FONT_SIZE_CLASS },
          t('canvasUI.components.templates.CTemplateManagement.actions.viewDetails'),
        ),
      key: 'viewDetails',
      icon: () => h(CIcon, { icon: 'circle-info' }),
    })

  if (!props.hideEdit) {
    menu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.edit')),
      key: 'edit',
      icon: () => h(CIcon, { icon: 'edit' }),
    })
  }

  if (!props.hideViewNewTab) {
    menu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.viewNewTab')),
      key: 'viewNewTab',
      icon: () => h(CIcon, { icon: 'browsers' }),
    })
  }

  if (
    !(props.hideView || props.hideViewDetails || props.hideViewNewTab || props.hideEdit) &&
    (!props.hideCopy ||
      !props.hideDelete ||
      !props.hideToggleDefault ||
      !props.hideMove ||
      !props.hideToggleStatus ||
      !props.hideLock)
  ) {
    menu.push({ key: 'd1', type: 'divider' })
  }

  if (!props.hideLock) {
    if (isLocked.value && (isLockedByMe.value || isOrgAdmin.value)) {
      // Only show unlock option if the current user is the one who locked it

      menu.push({
        label: () =>
          h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.unlock')),
        key: 'unlock',
        icon: () => h(CIcon, { icon: 'unlock' }),
      })
    } else if (!isLocked.value) {
      menu.push({
        label: () =>
          h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.lock')),
        key: 'lock',
        icon: () => h(CIcon, { icon: 'lock' }),
      })
    }
  }

  if (!props.hideMove) {
    menu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.move')),
      key: 'move',
      icon: () => h(CIcon, { icon: 'folder-arrow-up' }),
    })
  }

  if (!props.hideToggleStatus) {
    menu.push({
      label: () =>
        h(
          'div',
          { class: FONT_SIZE_CLASS },
          props.isDraft
            ? t('canvasUI.components.templates.CTemplateManagement.actions.setAsReady')
            : t('canvasUI.components.templates.CTemplateManagement.actions.setAsDraft'),
        ),
      key: 'toggleStatus',
      icon: () =>
        h(CIcon, {
          icon: props.isDraft ? 'check-circle' : 'circle-pause',
        }),
    })
  }

  if (!props.hideToggleDefault) {
    menu.push({
      label: () =>
        h('div', { class: 'flex justify-between items-center gap-4' }, [
          h(
            'div',
            { class: FONT_SIZE_CLASS, style: { color: props.isDefault ? themeVars.value.error : undefined } },
            props.isDefault
              ? t('canvasUI.components.templates.CTemplateManagement.actions.removeDefaultTemplate')
              : t('canvasUI.components.templates.CTemplateManagement.actions.setDefaultTemplate'),
          ),
          h(CTemplateManagementActionsCellIconWrapper, {
            icon: 'question',
            faType: 'fas',
            color: themeVars.value.primary6,
            iconColor: themeVars.value.primary,
            tooltip: t(
              'canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.defaultTemplateInfo',
            ),
          }),
        ]),
      key: 'toggleDefault',
      icon: () =>
        h(CIcon, {
          faType: 'fa-kit',
          icon: 'solid-presentation-screen-circle-check',
          color: props.isDefault ? themeVars.value.error : undefined,
        }),
    })
  }

  if (!props.hideCopy) {
    menu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.templates.CTemplateManagement.actions.duplicate')),
      key: 'copy',
      icon: () => h(CIcon, { icon: 'copy' }),
    })
  }

  if (!props.hideDelete) {
    menu.push({
      label: () =>
        h(
          'div',
          { class: FONT_SIZE_CLASS, style: { color: themeVars.value.error } },
          t('canvasUI.components.templates.CTemplateManagement.actions.delete'),
        ),
      key: 'delete',
      icon: () => h(CIcon, { icon: 'trash', color: themeVars.value.error }),
    })
  }

  return menu
})

function handleActions(key: string) {
  if (
    [
      'view',
      'viewNewTab',
      'viewDetails',
      'edit',
      'copy',
      'delete',
      'move',
      'createFromTemplate',
      'toggleDefault',
      'toggleStatus',
      'lock',
      'unlock',
    ].includes(key)
  ) {
    emit(key as keyof typeof emit)
  }
}
</script>

<template>
  <div class="flex justify-end gap-2">
    <CButton
      v-if="!hideCreateFromTemplate"
      v-tooltip="
        t('canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.createCanvasFromTemplate')
      "
      circle
      @click="handleActions('createFromTemplate')"
    >
      <template #icon>
        <CIcon icon="plus" size="16px" />
      </template>
    </CButton>

    <CButton
      v-if="!hideCopy"
      v-tooltip="t('canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.duplicate')"
      circle
      :loading="isCopying"
      @click="handleActions('copy')"
    >
      <template #icon>
        <CIcon icon="copy" size="16px" />
      </template>
    </CButton>

    <CButton
      v-if="!hideDelete"
      v-tooltip="t('canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.delete')"
      circle
      :loading="isDeleting"
      secondary
      type="error"
      @click="handleActions('delete')"
    >
      <template #icon>
        <CIcon icon="trash" size="16px" />
      </template>
    </CButton>

    <CDropdown v-if="actionsMenu.length > 0" :options="actionsMenu" trigger="click" @select="handleActions">
      <CButton
        v-tooltip="t('canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.actions')"
        circle
      >
        <template #icon>
          <CIcon icon="ellipsis" size="16px" />
        </template>
      </CButton>
    </CDropdown>
  </div>
</template>
